import React, { useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import './DeleteAccountForm.css';

interface DeleteAccountFormProps {
  username: string;
  onCancel: () => void;
}

const DeleteAccountForm: React.FC<DeleteAccountFormProps> = ({ username, onCancel }) => {
  const navigate = useNavigate();
  const [confirmationInput, setConfirmationInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyUsername = () => {
    navigator.clipboard.writeText(username);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isConfirmationValid = confirmationInput === username;

  const handleDelete = async () => {
    if (!isConfirmationValid) return;

    setLoading(true);
    setError('');

    try {
      const { apiService } = await import('../../services/api');
      const response = await apiService.deleteAccount();

      if (response.success) {
        alert(`Account deleted successfully!\n\n${response.message}`);
        await signOut();
        navigate('/');
      } else {
        setError(response.message || 'Failed to delete account');
      }
    } catch (err: any) {
      console.error('Delete account error:', err);
      setError(err.message || 'Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="delete-account-form">
      <h3>Delete Account</h3>
      
      <div className="warning-box">
        <strong>⚠️ Warning: This action cannot be undone!</strong>
        <p>
          Deleting your account will permanently remove all your data, including:
        </p>
        <ul>
          <li>All data crates and their contents</li>
          <li>All shared links</li>
          <li>Your profile information</li>
          <li>All account settings</li>
        </ul>
        <p>
          <strong>Note:</strong> You must delete all your crates before deleting your account.
        </p>
      </div>

      <div className="confirmation-section">
        <label htmlFor="username-confirmation">
          Type your username to confirm account deletion:
        </label>
        <div className="username-display" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong>{username}</strong>
          <button
            onClick={handleCopyUsername}
            title="Copy username"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.1rem', padding: '0.25rem 0.4rem',
              borderRadius: '4px', color: copied ? '#28a745' : '#666',
              transition: 'color 0.2s'
            }}
          >
            {copied ? '✓' : '📋'}
          </button>
        </div>
        <input
          id="username-confirmation"
          type="text"
          className="confirmation-input"
          placeholder="Enter your username"
          value={confirmationInput}
          onChange={(e) => setConfirmationInput(e.target.value)}
          disabled={loading}
        />
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDelete}
          disabled={!isConfirmationValid || loading}
        >
          {loading ? 'Deleting...' : 'Delete Account'}
        </button>
      </div>
    </div>
  );
};

export default DeleteAccountForm;
